/**
 * Settings persistence and scope resolution. Device and user layers are stored separately so
 * they can be exported as profiles; preset-scope values live in the scene.
 */
import type { PresetSettings, Scene } from '../shared/scene';
import {
  findSetting,
  migrateSettings,
  resolveSettings,
  sanitizeLayer,
  validateSetting,
  type Settings,
} from '../shared/settings';
import type { SyncKV } from './storage';

const DEVICE_KEY = 'glowtrope.settings.device.v1';
const USER_KEY = 'glowtrope.settings.user.v1';
const LEGACY_KEY = 'glowtrope.settings.v1';

export type SettingSource = 'default' | 'device' | 'user' | 'preset';

export class SettingsStore {
  private device: Partial<Settings>;
  private user: Partial<Settings>;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly kv: SyncKV,
    systemDefaults: Partial<Settings> = {},
  ) {
    const legacy = migrateSettings(kv.get(LEGACY_KEY));
    const storedDevice = kv.get(DEVICE_KEY);
    const storedUser = migrateSettings(kv.get(USER_KEY));
    this.device = sanitizeLayer(storedDevice ?? legacy, 'device');
    this.user = { ...systemDefaults, ...sanitizeLayer(storedUser ?? legacy, 'user') };
    // Preset-scope values from a legacy blob become user defaults.
    if (!storedUser && legacy) Object.assign(this.user, sanitizeLayer(legacy, 'preset'));
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Resolved settings for a scene. Preset-scope defaults come from the user layer. */
  resolve(scene?: Scene): Settings {
    return resolveSettings(this.device, this.user, (scene?.settings ?? {}) as Partial<Settings>);
  }

  sourceOf(key: keyof Settings, scene?: Scene): SettingSource {
    if (scene && key in scene.settings) return 'preset';
    if (key in this.user) return 'user';
    if (key in this.device) return 'device';
    return 'default';
  }

  /**
   * Set a value. Device/user values are stored here; preset-scope values are returned as a
   * scene setting for the caller to dispatch (they belong to the scene).
   */
  set<K extends keyof Settings>(
    key: K,
    value: Settings[K],
  ): { presetKey: keyof PresetSettings; value: number } | null {
    const def = findSetting(key);
    if (!def) return null;
    const v = validateSetting(def, value);
    if (v === undefined) return null;
    if (def.scope === 'preset')
      return { presetKey: key as keyof PresetSettings, value: v as number };
    const layer = def.scope === 'device' ? this.device : this.user;
    (layer as Record<string, unknown>)[key] = v;
    this.persist();
    return null;
  }

  /** Make a preset-scope value the default for scenes that don't set it. */
  setUserDefault<K extends keyof Settings>(key: K, value: Settings[K]): void {
    const def = findSetting(key);
    const v = def && validateSetting(def, value);
    if (v === undefined) return;
    (this.user as Record<string, unknown>)[key] = v;
    this.persist();
  }

  reset(keys: (keyof Settings)[]): void {
    for (const k of keys) {
      delete this.device[k];
      delete this.user[k];
    }
    this.persist();
  }

  exportProfile(): { device: Partial<Settings>; user: Partial<Settings> } {
    return { device: { ...this.device }, user: { ...this.user } };
  }

  importProfile(profile: unknown): void {
    const p = (profile ?? {}) as Record<string, unknown>;
    this.device = sanitizeLayer(p.device, 'device');
    this.user = { ...sanitizeLayer(p.user, 'user'), ...sanitizeLayer(p.user, 'preset') };
    this.persist();
  }

  private persist(): void {
    this.kv.set(DEVICE_KEY, this.device);
    this.kv.set(USER_KEY, this.user);
    for (const l of this.listeners) l();
  }
}
